/**
 * The project's setup (owner, 2026-11-03), in place of the old Agent runs
 * toggle: what turns its runs on, step by step.
 *
 * 1. The Sanctum agent harness, assigned by a Sanctum admin (directly or in
 *    a feature pack). Assigning it brings Pi for runs.
 * 2. CI wired: the default branch's pipelines include the agent component,
 *    so a failed job calls Sanctum. Not wired, or a configuration that
 *    doesn't validate, shows Sanctum's open MR when there is one, else the
 *    line to add and, with write access, "Add to .gitlab-ci.yml", which
 *    opens that MR as the viewer. No `.gitlab-ci.yml` shows the line only
 *    (Add edits an existing file). An include GitLab's lint doesn't see is
 *    said so ("named"). GitLab not answering is "couldn't check", with a
 *    retry, never "not wired".
 * 3. A schedule, informational only.
 */

import type { ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAddCiInclude } from '../hooks/useAddCiInclude';
import { projectPanelKeys } from '../hooks/useProjectPanel';
import { ciIncludeErrorMessage } from '../utils/setup';
import type { PanelResponse, PanelSetup } from '../types/projectPanel.types';

interface SetupStatusProps {
  projectId: string;
  panel: Pick<PanelResponse, 'access' | 'setup'>;
}

function Step(props: { done: boolean; name: string; children: ReactElement | string }): ReactElement {
  return (
    <li className={`setup-step${props.done ? ' setup-step--done' : ''}`}>
      <span className="setup-step__mark" aria-hidden="true">{props.done ? '✓' : '•'}</span>
      <span className="setup-step__name">{props.name}</span>
      <div className="setup-step__state">{props.children}</div>
    </li>
  );
}

/** A 422's message says to add the line by hand; it is shown just above. */
function AddInclude({ projectId }: { projectId: string }): ReactElement {
  const add = useAddCiInclude(projectId);
  return (
    <>
      <button
        type="button"
        className="secondary-button"
        disabled={add.isPending}
        onClick={() => { add.mutate(); }}
      >
        {add.isPending ? 'Opening the MR…' : 'Add to .gitlab-ci.yml (opens an MR as you)'}
      </button>
      {add.error !== null && (
        <div className="error-message" role="alert">{ciIncludeErrorMessage(add.error)}</div>
      )}
    </>
  );
}

const NOT_WIRED: Record<'not_wired' | 'invalid' | 'missing', string> = {
  not_wired: 'Not wired.',
  invalid: "The CI configuration doesn't validate: fix it, or add this line by hand.",
  missing: 'There is no .gitlab-ci.yml: add one with this include.',
};

function CiState(props: { projectId: string; setup: PanelSetup; write: boolean }): ReactElement {
  const { setup } = props;
  const client = useQueryClient();
  if (setup.ci === 'wired') return <span>Pipelines call Sanctum</span>;
  if (setup.ci === 'named') {
    return (
      <span>
        The include is in <code>.gitlab-ci.yml</code>, but GitLab&apos;s lint doesn&apos;t see it
        (check its rules).
      </span>
    );
  }
  if (setup.ci === 'unknown') {
    const retry = (): void => {
      void client.invalidateQueries({ queryKey: projectPanelKeys.panel(props.projectId), exact: true });
    };
    return (
      <span>
        Couldn&apos;t check{setup.ci_detail !== null ? `: ${setup.ci_detail}` : ''}.{' '}
        <button type="button" className="def-detail__link-button" onClick={retry}>Retry</button>
      </span>
    );
  }
  if (setup.ci_mr !== null) {
    return (
      <span>
        <a href={setup.ci_mr.url} target="_blank" rel="noreferrer">MR !{setup.ci_mr.iid} open</a>
        {' '}— CI is wired once it merges
      </span>
    );
  }
  const state = NOT_WIRED[setup.ci];
  if (setup.include_line === null) {
    return <span>{state} This Sanctum publishes no agent component to include.</span>;
  }
  return (
    <div>
      <span>{state} Add under <code>include:</code> in <code>.gitlab-ci.yml</code>:</span>
      <pre className="setup-step__line"><code>{setup.include_line}</code></pre>
      {props.write && setup.ci !== 'missing' && <AddInclude projectId={props.projectId} />}
    </div>
  );
}

/** Assigned and active is what runs need; an admin can deactivate an assigned harness. */
function harnessText(setup: PanelSetup): string {
  if (!setup.harness_assigned) {
    return 'Not assigned — a Sanctum admin assigns the Sanctum agent harness, directly or in a pack';
  }
  if (!setup.harness_active) {
    return 'Assigned, but the feature is inactive in the registry — no run starts until a Sanctum admin activates it';
  }
  return 'Assigned';
}

export function SetupStatus({ projectId, panel }: SetupStatusProps): ReactElement | null {
  const { setup } = panel;
  if (setup === null) return null;
  return (
    <section className="setup-status" aria-labelledby="setup-status-title">
      <h3 id="setup-status-title" className="def-list__title">Setup</h3>
      <ol className="setup-status__steps">
        <Step done={setup.harness_active} name="Sanctum agent harness">
          {harnessText(setup)}
        </Step>
        <Step done={setup.ci === 'wired'} name="CI wired">
          <CiState projectId={projectId} setup={setup} write={panel.access === 'write'} />
        </Step>
        <Step done={false} name="Schedule (optional)">
          A pipeline schedule in GitLab runs your scans without a push, so their failures start runs too.
        </Step>
      </ol>
    </section>
  );
}
